import type { LetterData } from "@/entities/Activity/types/AlphabetCards";
import ReloadIcon from "@rsuite/icons/Reload";
import type React from "react";
import { useEffect, useState } from "react";
import { FaRegPlayCircle } from "react-icons/fa";
import { IconButton } from "rsuite";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import style from "./AlphabetPractice.module.scss";

type Props = {
	data: LetterData[];
	activityId: string;
};

export const AlphabetPractice: React.FC<Props> = ({ data, activityId }) => {
	const [shuffledData, setShuffledData] = useState<LetterData[]>([]);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
	const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
	const [score, setScore] = useState(0);
	const [fireworksTrigger, setFireworksTrigger] = useState(false);

	useEffect(() => {
		setShuffledData([...data].sort(() => Math.random() - 0.5));
	}, [data]);

	const currentQuestion = shuffledData[currentQuestionIndex];

	const { triggerActivityCompletion } = useActivityCompletion();

	const getOptions = (correctAnswer: string) => {
		const allLetters = shuffledData
			.map((item) => item.letter)
			.filter(Boolean) as string[];
		const filteredOptions = allLetters.filter(
			(letter) => letter !== correctAnswer,
		);
		const shuffledOptions = [...filteredOptions]
			.sort(() => Math.random() - 0.5)
			.slice(0, 3);
		return [...shuffledOptions, correctAnswer].sort(() => Math.random() - 0.5);
	};

	const options = currentQuestion
		? getOptions(currentQuestion.letter || "")
		: [];

	const handleAnswer = (option: string) => {
		setSelectedAnswer(option);
		const correct = option === currentQuestion?.letter;
		setIsCorrect(correct);
		if (correct) setScore((prev) => prev + 1);
	};

	const handleNext = () => {
		if (currentQuestionIndex < shuffledData.length - 1) {
			setCurrentQuestionIndex((prev) => prev + 1);
			setSelectedAnswer(null);
			setIsCorrect(null);
		}
	};

	const handleReset = () => {
		setShuffledData([...data].sort(() => Math.random() - 0.5));
		setCurrentQuestionIndex(0);
		setSelectedAnswer(null);
		setIsCorrect(null);
		setScore(0);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (
			shuffledData.length > 0 &&
			currentQuestionIndex === shuffledData.length - 1
		) {
			setFireworksTrigger(true);
			triggerActivityCompletion(activityId, score, handleReset);
		}
	}, [currentQuestionIndex, shuffledData]);

	const handleFireworksComplete = () => {
		setFireworksTrigger(false);
	};

	if (!currentQuestion) {
		return <div>Loading...</div>;
	}

	return (
		<div className={style.alphabetPractice}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={handleFireworksComplete}
				/>
			)}
			<div className={style.header}>
				<div className={style.leftPart}>
					<span>
						Question {currentQuestionIndex + 1} / {shuffledData.length}
					</span>
					<IconButton
						onClick={handleReset}
						appearance="primary"
						icon={<ReloadIcon color="white" />}
					/>
				</div>
				<div className={style.scoreTracker}>Score: {score}</div>
			</div>
			<div className={style.questionContainer}>
				<button
					type="button"
					onClick={() => {
						if (currentQuestion.sound) {
							const audio = new Audio(currentQuestion.sound);
							audio.play();
						}
					}}
					className={style.soundButton}
				>
					<FaRegPlayCircle /> Play Sound
				</button>
			</div>
			<div className={style.optionsContainer}>
				{options.map((option) => {
					const img = data.find((item) => item.letter === option)?.image;
					// img = null
					return (
						<button
							type="button"
							key={option}
							className={`${style.optionButton} ${
								selectedAnswer === option
									? isCorrect
										? style.correct
										: style.incorrect
									: ""
							}`}
							onClick={() => handleAnswer(option)}
							disabled={!!selectedAnswer}
						>
							{img ? <img src={img} alt="letter" /> : option}
						</button>
					);
				})}
			</div>
			{selectedAnswer && (
				<div className={style.feedbackContainer}>
					{isCorrect ? (
						<p className={style.correctFeedback}>Correct! 🎉</p>
					) : (
						<p className={style.incorrectFeedback}>Incorrect. Try again!</p>
					)}
					{currentQuestionIndex < shuffledData.length - 1 ? (
						<button
							type="button"
							onClick={handleNext}
							className={style.nextButton}
						>
							Next
						</button>
					) : (
						<div className={style.completionMessage}>
							<p>
								Exercise Complete! Your final score: {score} /{" "}
								{shuffledData.length}
							</p>
							<button
								type="button"
								onClick={handleReset}
								className={style.resetButton}
							>
								Reset
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
